import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2, Circle, Target, Send, Trophy, Lightbulb, X, Clock, AlertTriangle, Eye, ZoomIn, BookOpen, Sparkles } from 'lucide-react';
import { studentAPI } from '../utils/api';
import { formatDate } from '../utils/helpers';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import RewardUnlockModal from '../components/RewardUnlockModal';
import soundManager from '../utils/soundManager';
import { PerformanceTrafficLight, SIGNAL_META } from '../components/PerformanceTrafficLight';
import { GabaritoCountdown } from '../components/GabaritoCountdown';
import { LiveEssayCoach } from '../components/LiveEssayCoach';

// Helper to format time remaining
const formatTimeRemaining = (seconds) => {
  if (seconds <= 0) return 'Expirado';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }
  return `${minutes}min`;
};

const StudentMissions = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMission, setActiveMission] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  
  // Reward modal state
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [rewardItem, setRewardItem] = useState(null);
  
  // Hint state - track which hints are showing
  const [showingHints, setShowingHints] = useState({}); // { questionIndex: true/false }
  const [wrongAttempts, setWrongAttempts] = useState({}); // { questionIndex: attemptCount }
  
  // Review state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);

  // Performance tracking
  const missionStartTime = useRef(null);
  const tabSwitchCount = useRef(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  const [zoomImage, setZoomImage] = useState(null);
  const [essayAnswers, setEssayAnswers] = useState({});
  const [essayImages, setEssayImages] = useState({});
  const [uploadingEssayImage, setUploadingEssayImage] = useState(null);

  useEffect(() => {
    loadMissions();
  }, []);

  // Open review automatically when arriving via `?review=<mission_id>` deep link
  // (used by the EvolutionHistory traffic-light cards).
  useEffect(() => {
    const target = searchParams.get('review');
    if (!target || loading || !missions.length) return;
    const m = missions.find((mi) => mi.mission_id === target);
    if (m) {
      openMissionReview(m);
      // Strip the query param so the modal doesn't re-open on subsequent navigations
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missions, loading, searchParams]);

  // Start/stop timer when mission is active
  useEffect(() => {
    if (activeMission && !result) {
      missionStartTime.current = Date.now();
      tabSwitchCount.current = 0;
      setElapsedTime(0);
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - missionStartTime.current) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeMission, result]);

  // Visibility API: track tab switches
  useEffect(() => {
    if (!activeMission || result) return;
    const handleVisibility = () => {
      if (document.hidden) {
        tabSwitchCount.current += 1;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [activeMission, result]);

  const loadMissions = async (opts = {}) => {
    try {
      const response = await studentAPI.getMissions();
      setMissions(response.data);
    } catch (error) {
      console.error('Failed to load missions:', error);
      if (!opts.silent) {
        toast.error('Erro ao carregar missões');
      }
    } finally {
      setLoading(false);
    }
  };

  // Load mission review/correction
  const openMissionReview = async (mission) => {
    setLoadingReview(true);
    setReviewDialogOpen(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student/missions/${mission.mission_id}/review`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to load review');
      
      const data = await response.json();
      setReviewData(data);
    } catch (error) {
      console.error('Failed to load review:', error);
      toast.error('Erro ao carregar correção');
      setReviewDialogOpen(false);
    } finally {
      setLoadingReview(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Draft autosave: keep the student's answers in localStorage so a
  // page refresh / mid-mission error doesn't wipe their progress.
  // ─────────────────────────────────────────────────────────────
  const draftKey = activeMission ? `mission_draft_${activeMission.mission_id}` : null;

  useEffect(() => {
    if (!draftKey || result) return;
    try {
      // Only save if there's something to save
      if (Object.keys(answers).length === 0 && Object.keys(essayAnswers).length === 0 && Object.keys(essayImages).length === 0) {
        return;
      }
      localStorage.setItem(draftKey, JSON.stringify({
        answers,
        essayAnswers,
        essayImages,
        savedAt: Date.now(),
      }));
    } catch (e) {
      // localStorage may be unavailable (private mode) — silent fail.
    }
  }, [draftKey, answers, essayAnswers, essayImages, result]);

  const handleStartMission = (mission) => {
    setActiveMission(mission);
    setResult(null);
    setShowingHints({});
    setWrongAttempts({});

    // Try to restore a previously saved draft
    let restored = false;
    try {
      const raw = localStorage.getItem(`mission_draft_${mission.mission_id}`);
      if (raw) {
        const d = JSON.parse(raw);
        // Drafts older than 7 days are discarded
        const stale = d.savedAt && (Date.now() - d.savedAt) > 7 * 24 * 60 * 60 * 1000;
        if (!stale) {
          setAnswers(d.answers || {});
          setEssayAnswers(d.essayAnswers || {});
          setEssayImages(d.essayImages || {});
          restored = true;
        } else {
          localStorage.removeItem(`mission_draft_${mission.mission_id}`);
        }
      }
    } catch (e) {
      // ignore
    }
    if (!restored) {
      setAnswers({});
      setEssayAnswers({});
      setEssayImages({});
    } else {
      toast.success('Rascunho restaurado — continue de onde parou!', { icon: '💾', duration: 3500 });
    }
  };

  const handleSelectAnswer = (questionIndex, answer) => {
    const question = activeMission.questions[questionIndex];
    const previousAnswer = answers[questionIndex];
    
    // Show hint if student changes their answer (indicating uncertainty) and hint is available
    if (question.hint && previousAnswer && previousAnswer !== answer && !showingHints[questionIndex]) {
      setShowingHints(prev => ({ ...prev, [questionIndex]: true }));
      soundManager.playNotificationAlert();
      toast.info('Dica de Mestre disponível! 💡', { duration: 2000 });
    }
    
    // Track attempts
    const attempts = (wrongAttempts[questionIndex] || 0) + 1;
    setWrongAttempts(prev => ({ ...prev, [questionIndex]: attempts }));
    
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const handleCloseHint = (questionIndex) => {
    setShowingHints(prev => ({ ...prev, [questionIndex]: false }));
  };

  const handleSubmitMission = async () => {
    if (!activeMission) return;
    
    const questions = activeMission.questions;
    const mcQuestions = questions.filter((q, i) => (q.question_type || 'multiple_choice') === 'multiple_choice');
    const essayQuestions = questions.filter((q, i) => q.question_type === 'essay');
    const mcIndices = questions.map((q, i) => (q.question_type || 'multiple_choice') === 'multiple_choice' ? i : null).filter(i => i !== null);
    const essayIndices = questions.map((q, i) => q.question_type === 'essay' ? i : null).filter(i => i !== null);

    // Validate all answered
    const mcAnswered = mcIndices.every(i => answers[i]);
    const essayAnswered = essayIndices.every(i => (essayAnswers[i] || '').trim().length > 0);
    
    if (!mcAnswered || !essayAnswered) {
      toast.error('Responda todas as perguntas antes de enviar');
      return;
    }
    
    const timeSpent = missionStartTime.current ? Math.floor((Date.now() - missionStartTime.current) / 1000) : 0;
    
    setSubmitting(true);
    try {
      let mcResult = null;
      let essayResult = null;

      // Submit MC answers if any
      if (mcQuestions.length > 0) {
        const answersArray = [];
        for (let i = 0; i < questions.length; i++) {
          answersArray.push(answers[i] || '');
        }
        const response = await studentAPI.submitMission(activeMission.mission_id, answersArray, timeSpent, tabSwitchCount.current);
        mcResult = response.data;
      }

      // Submit essay answers if any
      if (essayQuestions.length > 0) {
        const essayPayload = {};
        essayIndices.forEach(i => { essayPayload[i] = essayAnswers[i] || ''; });
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student/missions/${activeMission.mission_id}/submit-essay`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ essay_answers: essayPayload, essay_images: essayImages, time_seconds: timeSpent, tab_switches: tabSwitchCount.current })
        });
        if (resp.ok) essayResult = await resp.json();
      }

      // Build combined result
      if (mcResult) {
        if (essayQuestions.length > 0) {
          mcResult.has_essays = true;
          mcResult.essay_message = 'Suas respostas dissertativas foram enviadas para avaliação do professor.';
        }
        setResult(mcResult);
        
        const { correct_count, total_questions, xp_earned, bonus_xp, win_streak, streak_bonus_triggered, combo_level_reached, perfect_score } = mcResult;
      
      // Show celebration based on performance
      if (perfect_score) {
        // Epic confetti for perfect score
        confetti({
          particleCount: 200,
          spread: 100,
          origin: { y: 0.6 },
          colors: streak_bonus_triggered ? ['#FFD700', '#FFA500', '#FF6347'] : undefined
        });
        
        // Show streak celebration
        if (streak_bonus_triggered) {
          if (combo_level_reached >= 5) {
            // Epic victory for combo 5+
            playVictorySound();
            const duration = 2000;
            const end = Date.now() + duration;
            const frame = () => {
              confetti({
                particleCount: 5,
                angle: 60,
                spread: 55,
                origin: { x: 0 },
                colors: ['#FFD700', '#FF6347', '#9333EA']
              });
              confetti({
                particleCount: 5,
                angle: 120,
                spread: 55,
                origin: { x: 1 },
                colors: ['#FFD700', '#FF6347', '#9333EA']
              });
              if (Date.now() < end) requestAnimationFrame(frame);
            };
            frame();
            toast.success(`🔥🔥🔥 COMBO MÁXIMO x${win_streak}! +${bonus_xp} AURA BÔNUS! 🔥🔥🔥`, { duration: 5000 });
          } else {
            toast.success(`🔥 Combo x${win_streak}! +${bonus_xp} AURA de bônus!`, { duration: 4000 });
          }
        } else {
          toast.success('Perfeito! Todas as respostas corretas! 🎉');
        }
        
        // Show reward modal if item was won
        if (mcResult.reward_item) {
          // Delay to let confetti finish first
          setTimeout(() => {
            setRewardItem(mcResult.reward_item);
            setShowRewardModal(true);
          }, 1500);
        }
      } else if (correct_count > 0) {
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.7 }
        });
        toast.success(`+${xp_earned} XP!`);
        
        // Show streak reset warning if they had one
        if (win_streak === 0) {
          toast.info('Combo resetado. Acerte todas para iniciar um novo!', { duration: 3000 });
        }
      }
      
      if (mcResult.level_up) {
        setTimeout(() => {
          confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 }
          });
          toast.success(`Voce subiu para o nivel ${mcResult.new_level}!`, { duration: 5000 });
        }, 1000);
      }
      } else if (essayResult) {
        // Essay-only mission
        setResult({ essay_only: true, has_essays: true, essay_message: essayResult.message || 'Respostas enviadas para avaliação!' });
        toast.success('Respostas dissertativas enviadas! Aguarde a avaliação do professor.');
      }

      // Submit succeeded — clear the autosaved draft for this mission.
      try {
        if (activeMission?.mission_id) {
          localStorage.removeItem(`mission_draft_${activeMission.mission_id}`);
        }
      } catch (e) { /* ignore */ }

      // Reload missions silently — a transient failure here must NOT show
      // "Erro ao carregar missões" since the submission itself succeeded.
      loadMissions({ silent: true });
    } catch (error) {
      console.error('Failed to submit mission:', error);
      const status = error?.response?.status;
      if (status === 400) {
        toast.error(error.response?.data?.detail || 'Missão já completada');
        // 400 means the server already accepted a previous submission — safe to clear draft.
        try {
          if (activeMission?.mission_id) localStorage.removeItem(`mission_draft_${activeMission.mission_id}`);
        } catch (e) { /* ignore */ }
      } else if (status === 401 || status === 403) {
        toast.error('Sua sessão expirou. Suas respostas estão salvas — faça login novamente e volte para esta missão.', { duration: 7000 });
      } else {
        toast.error('Falha ao enviar respostas. Suas respostas estão salvas — tente novamente em alguns segundos.', { duration: 6000 });
      }
      // IMPORTANT: do NOT close the dialog or clear answers on failure.
      // The draft remains in localStorage and React state, so the student can retry.
    } finally {
      setSubmitting(false);
    }
  };
  
  // Play victory sound for combo 5
  const playVictorySound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      // Epic victory arpeggio
      const notes = [523, 659, 784, 1047, 1319, 1568];
      notes.forEach((freq, i) => {
        oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + i * 0.1);
      });
      
      gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.8);
    } catch (e) {
      console.log('Audio not available');
    }
  };

  const handleCloseMission = () => {
    setActiveMission(null);
    setAnswers({});
    setResult(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  const incompleteMissions = missions.filter(m => !m.completed);
  const completedMissions = missions.filter(m => m.completed);

  // If viewing a mission
  if (activeMission) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <nav className="bg-white border-b border-slate-200">
          <div className="container mx-auto px-4 py-4">
            <Button
              data-testid="back-to-missions-btn"
              onClick={handleCloseMission}
              variant="ghost"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar às Missões
            </Button>
          </div>
        </nav>

        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-outfit font-bold text-slate-900">{activeMission.title}</h1>
                <p className="text-slate-600 font-nunito">{activeMission.description}</p>

                {/* Lab References */}
                {activeMission.lab_references?.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <span className="text-xs text-teal-600 font-bold">Material de apoio:</span>
                    {activeMission.lab_references.map(ref => (
                      <button
                        key={ref.material_id}
                        onClick={() => navigate('/student/lab')}
                        className="flex items-center gap-1 px-2 py-0.5 bg-teal-50 border border-teal-200 rounded-full text-[11px] text-teal-700 font-bold hover:bg-teal-100 transition-colors"
                        data-testid={`lab-ref-link-${ref.material_id}`}
                      >
                        <BookOpen className="w-3 h-3" />
                        {ref.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Timer bar */}
            {!result && (
              <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-2 mb-4 border border-slate-200">
                <div className="flex items-center gap-2 text-sm font-nunito text-slate-600" data-testid="mission-timer">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="font-bold tabular-nums">
                    {Math.floor(elapsedTime / 60).toString().padStart(2, '0')}:{(elapsedTime % 60).toString().padStart(2, '0')}
                  </span>
                </div>
                {tabSwitchCount.current > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 font-bold" data-testid="tab-switch-indicator">
                    <Eye className="w-3.5 h-3.5" />
                    {tabSwitchCount.current} troca{tabSwitchCount.current > 1 ? 's' : ''} de aba
                  </div>
                )}
              </div>
            )}

            {result ? (
              // Show result
              <div className="text-center py-8">
                <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
                  result.correct_count === result.total_questions 
                    ? 'bg-green-100' 
                    : result.correct_count > 0 
                      ? 'bg-yellow-100' 
                      : 'bg-red-100'
                }`}>
                  {result.correct_count === result.total_questions ? (
                    <Trophy className="w-12 h-12 text-green-600" />
                  ) : (
                    <Target className="w-12 h-12 text-yellow-600" />
                  )}
                </div>
                
                <h2 className="text-3xl font-outfit font-bold text-slate-900 mb-2">
                  {result.correct_count === result.total_questions 
                    ? 'Perfeito!' 
                    : result.correct_count > 0 
                      ? 'Bom trabalho!' 
                      : 'Tente novamente!'}
                </h2>
                
                <p className="text-xl text-slate-600 font-nunito mb-6">
                  Você acertou <span className="font-bold text-primary">{result.correct_count}</span> de{' '}
                  <span className="font-bold">{result.total_questions}</span> questões
                </p>
                
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-8 py-4 rounded-2xl font-bold text-2xl inline-block shadow-lg mb-4">
                  +{result.xp_earned} XP
                </div>
                
                {/* Coins Earned Breakdown */}
                {result.coins_earned > 0 && (
                  <div className="mb-6">
                    <div className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white px-8 py-4 rounded-2xl font-bold text-2xl inline-flex items-center gap-3 shadow-lg" style={{ animation: 'coinBounce 0.6s ease-out' }}>
                      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M8 10h8M8 14h8"/></svg>
                      +{result.coins_earned} Coins
                    </div>
                    <p className="text-sm text-slate-500 mt-2 font-nunito">
                      {result.correct_count} acertos x {result.coins_per_question || 0} coins
                      {result.coin_perfect_bonus > 0 && (
                        <span className="text-amber-600 font-bold"> + {result.coin_perfect_bonus} bonus gabarito!</span>
                      )}
                    </p>
                    <style>{`@keyframes coinBounce { 0% { transform: scale(0.5); opacity: 0; } 60% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }`}</style>
                  </div>
                )}
                
                {result.level_up && (
                  <div className="bg-purple-100 text-purple-800 px-6 py-3 rounded-xl font-bold mb-6">
                    Voce subiu para o nivel {result.new_level}!
                  </div>
                )}
                
                {result.new_badges?.length > 0 && (
                  <div className="space-y-2 mb-6">
                    {result.new_badges.map((badge, i) => (
                      <div key={i} className="bg-gradient-to-r from-indigo-100 to-purple-100 border border-indigo-200 px-6 py-3 rounded-xl font-bold flex items-center gap-3">
                        <span className="text-2xl">{badge.emoji}</span>
                        <div>
                          <p className="text-indigo-800 text-sm">Nova Badge: {badge.name}!</p>
                          <p className="text-indigo-600 text-xs font-normal">{badge.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {result.has_essays && (
                  <div className="bg-violet-50 border border-violet-200 px-6 py-3 rounded-xl mb-6" data-testid="essay-pending-notice">
                    <p className="text-violet-700 text-sm font-bold">{result.essay_message || 'Respostas dissertativas enviadas para avaliação!'}</p>
                    <p className="text-violet-500 text-xs mt-1">A AURA das questões dissertativas será liberada após a avaliação do professor.</p>
                  </div>
                )}

                {/* Gabarito unlock countdown — show right after submission */}
                {activeMission?.gabarito_unlock_at && !activeMission?.gabarito_released && (
                  <div className="max-w-md mx-auto mb-6 text-left" data-testid="post-submit-countdown">
                    <GabaritoCountdown unlockAt={activeMission.gabarito_unlock_at} />
                  </div>
                )}
                
                <Button
                  onClick={handleCloseMission}
                  className="bg-primary hover:bg-primary/90 text-white h-12 px-8 rounded-xl font-bold"
                >
                  Voltar às Missões
                </Button>
              </div>
            ) : (
              // Show questions
              <div className="space-y-8">
                {activeMission.questions.map((question, qIndex) => (
                  <div key={qIndex} className={`border-2 rounded-xl p-6 ${question.question_type === 'essay' ? 'border-violet-200 bg-violet-50/30' : 'border-slate-100'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-outfit font-bold text-slate-900">
                          Pergunta {qIndex + 1}
                        </h3>
                        {question.question_type === 'essay' && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-violet-100 text-violet-700 uppercase tracking-wider" data-testid={`question-type-badge-essay-${qIndex}`}>
                            Dissertativa
                          </span>
                        )}
                        {question.bloom_level && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                            question.bloom_level === 'Conhecimento' || question.bloom_level === 'Compreensão' ? 'bg-blue-100 text-blue-700' :
                            question.bloom_level === 'Aplicação' || question.bloom_level === 'Análise' ? 'bg-orange-100 text-orange-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {question.bloom_level}
                          </span>
                        )}
                      </div>
                      <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-lg text-sm font-bold">
                        {question.xp_value} AURA
                      </span>
                    </div>
                    
                    <p className="text-slate-700 font-nunito mb-4">{question.question}</p>
                    
                    {/* Question Image */}
                    {question.image_url && (
                      <div className="mb-4">
                        <div
                          className="relative group cursor-pointer rounded-xl overflow-hidden border border-slate-200 inline-block"
                          onClick={() => setZoomImage({ url: question.image_url, alt: question.image_alt || '' })}
                          data-testid={`question-image-${qIndex}`}
                        >
                          <img
                            src={question.image_url}
                            alt={question.image_alt || 'Imagem da questão'}
                            className="max-h-64 w-auto object-contain"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
                              <ZoomIn className="w-5 h-5 text-slate-700" />
                            </div>
                          </div>
                        </div>
                        {question.image_alt && (
                          <p className="text-[10px] text-slate-400 mt-1 italic">{question.image_alt}</p>
                        )}
                      </div>
                    )}
                    
                    {/* Hint Bubble - Shows when student got wrong answer */}
                    {showingHints[qIndex] && question.hint && (
                      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl relative animate-fade-in">
                        <button
                          onClick={() => handleCloseHint(qIndex)}
                          className="absolute top-2 right-2 p-1 rounded-full hover:bg-amber-100 transition-colors"
                        >
                          <X className="w-4 h-4 text-amber-600" />
                        </button>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0">
                            <Lightbulb className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-bold text-amber-800 text-sm mb-1">Dica de Mestre</h4>
                            <p className="text-amber-700 text-sm">{question.hint}</p>
                          </div>
                        </div>
                        <div className="absolute -bottom-2 left-8 w-4 h-4 bg-amber-50 border-b border-r border-amber-200 transform rotate-45" />
                      </div>
                    )}
                    
                    {/* Multiple Choice Options */}
                    {(question.question_type || 'multiple_choice') === 'multiple_choice' && (
                      <div className="space-y-2">
                        {['A', 'B', 'C', 'D'].map((option) => {
                          const optionKey = `option_${option.toLowerCase()}`;
                          const isSelected = answers[qIndex] === option;
                          const hasOption = question[optionKey] && question[optionKey].trim() !== '';
                          
                          if (!hasOption) return null;
                          
                          return (
                            <button
                              key={option}
                              data-testid={`question-${qIndex}-option-${option}`}
                              onClick={() => handleSelectAnswer(qIndex, option)}
                              className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                                isSelected
                                  ? 'border-primary bg-primary/5'
                                  : 'border-slate-200 hover:border-primary/50 hover:bg-slate-50'
                              }`}
                            >
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                isSelected
                                  ? 'bg-primary text-white'
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {option}
                              </span>
                              <span className="font-nunito text-slate-700">{question[optionKey]}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Essay Answer */}
                    {question.question_type === 'essay' && (
                      <div className="space-y-2" data-testid={`essay-area-${qIndex}`}>
                        {/* Key concepts hint (read-only, no checklist) */}
                        {Array.isArray(question.key_concepts) && question.key_concepts.length > 0 && (
                          <div
                            data-testid={`essay-keyconcepts-${qIndex}`}
                            className="rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200 p-3"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                              <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">
                                Palavras-chave esperadas pelo Mestre
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {question.key_concepts.map((c, ci) => (
                                <span
                                  key={`kc-${qIndex}-${ci}`}
                                  className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full bg-white text-indigo-700 border border-indigo-200"
                                >
                                  {c}
                                </span>
                              ))}
                            </div>
                            <p className="text-[10px] text-indigo-500 italic mt-2">
                              Use-as como guia — sua resposta deve abordar esses conceitos com suas próprias palavras.
                            </p>
                          </div>
                        )}

                        {/* Live coach: traffic light updates while the student types */}
                        {Array.isArray(question.key_concepts) && question.key_concepts.length > 0 && (
                          <LiveEssayCoach
                            text={essayAnswers[qIndex] || ''}
                            keyConcepts={question.key_concepts}
                            testIdPrefix={`essay-coach-${qIndex}`}
                          />
                        )}

                        <textarea
                          data-testid={`essay-input-${qIndex}`}
                          value={essayAnswers[qIndex] || ''}
                          onChange={(e) => setEssayAnswers(prev => ({ ...prev, [qIndex]: e.target.value }))}
                          placeholder="Escreva sua resposta aqui..."
                          rows={6}
                          className="w-full p-4 rounded-xl border-2 border-slate-200 bg-white focus:border-violet-400 focus:ring-1 focus:ring-violet-200 outline-none font-nunito text-slate-700 text-sm resize-y transition-colors"
                        />
                        {/* Image upload for essay */}
                        <div className="flex items-center gap-3">
                          {essayImages[qIndex] ? (
                            <div className="relative group">
                              <img src={essayImages[qIndex]} alt="Anexo" className="h-16 rounded-lg border object-cover" />
                              <button
                                onClick={() => setEssayImages(prev => { const n = {...prev}; delete n[qIndex]; return n; })}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed text-xs cursor-pointer transition-colors ${uploadingEssayImage === qIndex ? 'border-violet-400 text-violet-500' : 'border-slate-300 text-slate-500 hover:border-violet-400 hover:text-violet-500'}`}>
                              {uploadingEssayImage === qIndex ? (
                                <><span className="w-3 h-3 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" /> Enviando...</>
                              ) : (
                                <><ZoomIn className="w-3.5 h-3.5" /> Anexar Imagem</>
                              )}
                              <input
                                type="file" accept="image/*" className="hidden"
                                disabled={uploadingEssayImage === qIndex}
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  setUploadingEssayImage(qIndex);
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  try {
                                    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/missions/upload-image`, { method: 'POST', credentials: 'include', body: formData });
                                    if (resp.ok) { const d = await resp.json(); setEssayImages(prev => ({ ...prev, [qIndex]: d.image_url })); toast.success('Imagem anexada!'); }
                                    else toast.error('Erro no upload');
                                  } catch { toast.error('Erro ao enviar'); }
                                  finally { setUploadingEssayImage(null); }
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          )}
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span>{(essayAnswers[qIndex] || '').length} caracteres</span>
                            <span className="text-violet-500 font-bold">Dissertativa</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                  <p className="text-slate-600 font-nunito">
                    {(() => {
                      const mcCount = activeMission.questions.filter((q, i) => (q.question_type || 'multiple_choice') === 'multiple_choice' && answers[i]).length;
                      const essayCount = activeMission.questions.filter((q, i) => q.question_type === 'essay' && (essayAnswers[i] || '').trim()).length;
                      const total = mcCount + essayCount;
                      return `${total} de ${activeMission.questions.length} respondidas`;
                    })()}
                  </p>
                  <Button
                    data-testid="submit-mission-btn"
                    onClick={handleSubmitMission}
                    disabled={submitting}
                    className="bg-accent hover:bg-accent/90 text-white h-12 px-8 rounded-xl font-bold"
                  >
                    {submitting ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Enviar Respostas
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Image Zoom Modal - inside activeMission view */}
        {zoomImage && (
          <div
            className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-pointer"
            onClick={() => setZoomImage(null)}
            data-testid="image-zoom-modal"
          >
            <button
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors"
              onClick={() => setZoomImage(null)}
              data-testid="zoom-modal-close-btn"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <img
              src={zoomImage.url}
              alt={zoomImage.alt}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            {zoomImage.alt && (
              <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/50 px-4 py-1 rounded-full">
                {zoomImage.alt}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Mission list view
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/student/dashboard')}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-2 flex items-center gap-3" data-testid="missions-title">
            <Target className="w-8 h-8 text-purple-600" />
            Missões
          </h1>
          <p className="text-slate-600 font-nunito">Complete missões de múltipla escolha para ganhar AURA</p>
        </div>

        {/* Pending Missions */}
        <div className="mb-12">
          <h2 className="text-xl font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Circle className="w-5 h-5 text-purple-600" />
            Missões Disponíveis ({incompleteMissions.length})
          </h2>
          
          {incompleteMissions.length === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-12 text-center">
              <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-nunito">Nenhuma missão disponível no momento</p>
            </div>
          ) : (
            <div className="space-y-4">
              {incompleteMissions.map((mission) => {
                const expInfo = mission.expiration_info || {};
                const isUrgent = expInfo.is_urgent && !expInfo.expired;
                
                return (
                <div
                  key={mission.mission_id}
                  data-testid="pending-mission-card"
                  className={`bg-white rounded-2xl border-2 p-6 shadow-sm hover:shadow-md transition-all duration-200 ${
                    isUrgent 
                      ? 'border-orange-300 bg-gradient-to-r from-orange-50 to-red-50 animate-pulse-subtle' 
                      : 'border-slate-100 hover:border-purple-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Target className={`w-5 h-5 ${isUrgent ? 'text-orange-500' : 'text-purple-600'}`} />
                        <h3 className="text-lg font-outfit font-bold text-slate-900">{mission.title}</h3>
                        {mission.classroom_name && (
                          <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-lg text-xs font-bold">
                            {mission.classroom_name}
                          </span>
                        )}
                        
                        {/* Urgency indicator */}
                        {isUrgent && (
                          <span className="flex items-center gap-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse shadow-lg">
                            <AlertTriangle className="w-3 h-3" />
                            Apresse-se, Treinador!
                          </span>
                        )}
                      </div>
                      <p className="text-slate-600 font-nunito mb-3">{mission.description}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500 font-nunito">
                        <span>{mission.questions.length} {mission.questions.length === 1 ? 'pergunta' : 'perguntas'}</span>
                        
                        {/* Time remaining indicator */}
                        {expInfo.has_expiry && !expInfo.expired && (
                          <span className={`flex items-center gap-1 ${isUrgent ? 'text-red-500 font-bold' : 'text-slate-500'}`}>
                            <Clock className={`w-4 h-4 ${isUrgent ? 'animate-pulse' : ''}`} />
                            {formatTimeRemaining(expInfo.remaining_seconds)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="ml-6 flex flex-col items-end gap-3">
                      <div className={`text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg ${
                        isUrgent 
                          ? 'bg-gradient-to-r from-orange-500 to-red-500' 
                          : 'bg-gradient-to-r from-purple-500 to-pink-500'
                      }`}>
                        até {mission.total_xp} AURA
                      </div>
                      <Button
                        data-testid="start-mission-btn"
                        onClick={() => handleStartMission(mission)}
                        className={`h-10 px-6 rounded-xl font-bold transition-all ${
                          isUrgent 
                            ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                            : 'bg-purple-600 hover:bg-purple-700 text-white'
                        }`}
                      >
                        {isUrgent ? '⚡ Iniciar Agora!' : 'Iniciar Missão'}
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Completed Missions */}
        {completedMissions.length > 0 && (
          <div>
            <h2 className="text-xl font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Missões Completadas ({completedMissions.length})
            </h2>
            <div className="space-y-4">
              {completedMissions.map((mission) => (
                <div
                  key={mission.mission_id}
                  data-testid="completed-mission-card"
                  className="bg-white rounded-2xl border-2 border-green-100 p-6 shadow-sm"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <CheckCircle2 className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-outfit font-bold text-slate-900">{mission.title}</h3>
                          {mission.classroom_name && (
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-lg text-xs font-bold">
                              {mission.classroom_name}
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 font-nunito mb-2">{mission.description}</p>
                        {mission.completion && (
                          <div className="flex items-center gap-4">
                            <p className="text-sm text-green-600 font-nunito font-bold">
                              {mission.completion.correct_count}/{mission.completion.total_questions} acertos
                            </p>
                            <p className="text-sm text-slate-500 font-nunito">
                              {formatDate(mission.completion.completed_at)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="bg-green-100 text-green-700 px-4 py-2 rounded-xl font-bold">
                        +{mission.completion?.xp_earned || 0} XP
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openMissionReview(mission)}
                        className={`${mission.gabarito_released ? 'text-blue-600 border-blue-300 hover:bg-blue-50' : 'text-amber-700 border-amber-300 hover:bg-amber-50'}`}
                        data-testid={`view-review-btn-${mission.mission_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {mission.gabarito_released ? 'Ver Correção' : 'Ver Resumo'}
                      </Button>
                      {!mission.gabarito_released && mission.gabarito_unlock_at && (
                        <GabaritoCountdown unlockAt={mission.gabarito_unlock_at} variant="compact" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Reward Unlock Modal */}
      <RewardUnlockModal
        isOpen={showRewardModal}
        onClose={() => {
          setShowRewardModal(false);
          setRewardItem(null);
        }}
        rewardItem={rewardItem}
      />
      
      {/* Mission Review Dialog */}
      {reviewDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-outfit font-bold flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  {reviewData?.gabarito_locked ? 'Resumo da Missão' : 'Correção da Missão'}
                </h2>
                <p className="text-slate-600 text-sm">{reviewData?.mission_title}</p>
              </div>
              <button 
                onClick={() => { setReviewDialogOpen(false); setReviewData(null); }}
                className="p-2 hover:bg-slate-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {loadingReview ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : reviewData && (
              <div className="p-4 space-y-4">
                {/* Summary */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold text-purple-700">
                      {reviewData.correct_count}/{reviewData.total_questions}
                    </div>
                    <div className="text-sm text-purple-600">Acertos</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">
                      +{reviewData.xp_earned} XP
                    </div>
                    {reviewData.bonus_xp > 0 && (
                      <div className="text-sm text-orange-600">+{reviewData.bonus_xp} bônus</div>
                    )}
                  </div>
                </div>

                {/* Essay signals breakdown */}
                {reviewData.essay_signals &&
                  (reviewData.essay_signals.green + reviewData.essay_signals.yellow + reviewData.essay_signals.red) > 0 && (
                    <div
                      data-testid="essay-signals-breakdown"
                      className="rounded-xl p-3 bg-slate-900 text-white flex items-center justify-between gap-3"
                    >
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-300">
                        Semáforo de Desempenho
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
                          <span className="text-sm font-bold" data-testid="signal-green-count">
                            {reviewData.essay_signals.green}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded-full bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.8)]" />
                          <span className="text-sm font-bold" data-testid="signal-yellow-count">
                            {reviewData.essay_signals.yellow}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded-full bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.8)]" />
                          <span className="text-sm font-bold" data-testid="signal-red-count">
                            {reviewData.essay_signals.red}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Gabarito locked — show countdown instead of detailed correction */}
                {reviewData.gabarito_locked && (
                  <GabaritoCountdown unlockAt={reviewData.gabarito_unlock_at} />
                )}
                
                {/* Questions */}
                {reviewData.questions?.map((q, idx) => {
                  // ESSAY QUESTION — Semáforo de Desempenho
                  if (q.question_type === 'essay') {
                    const graded = q.graded;
                    const effectiveSignal = (() => {
                      if (graded && typeof q.teacher_score === 'number') {
                        if (q.teacher_score >= 70) return 'green';
                        if (q.teacher_score >= 40) return 'yellow';
                        return 'red';
                      }
                      if (q.submission_status === 'not_submitted') return null;
                      // Pre-AI analysis exists even before teacher grades
                      return q.ai_signal || 'pending';
                    })();
                    const signalKey = effectiveSignal || 'pending';
                    const meta = SIGNAL_META[signalKey] || SIGNAL_META.pending;
                    const pendingTeacher = !graded;

                    return (
                      <div
                        key={`review-essay-${q.question_number}-${idx}`}
                        data-testid={`review-essay-${idx}`}
                        className={`relative rounded-2xl border-2 ${meta.border} bg-white overflow-hidden`}
                      >
                        {/* Accent gradient stripe */}
                        <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${meta.gradient}`} />

                        <div className="p-4 pl-6 space-y-4">
                          {/* Header: question + semaforo */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                  Dissertativa
                                </span>
                                {q.bloom_level && (
                                  <span className="text-[10px] font-bold uppercase tracking-widest text-violet-500 bg-violet-50 px-2 py-0.5 rounded-full">
                                    {q.bloom_level}
                                  </span>
                                )}
                                {pendingTeacher && (
                                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                    Pré-análise IA
                                  </span>
                                )}
                              </div>
                              <h3 className="font-bold text-slate-800 leading-snug">
                                {q.question_number}. {q.question_text}
                              </h3>
                            </div>
                            <PerformanceTrafficLight signal={effectiveSignal} size="md" />
                          </div>

                          {/* Student answer */}
                          <div className="ml-0">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                              Sua resposta
                            </div>
                            <div className={`relative rounded-xl p-3 ${meta.soft} border ${meta.border}`}>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {q.student_text || <em className="text-slate-400">Sem resposta textual</em>}
                              </p>
                              {q.student_image_url && (
                                <button
                                  className="mt-2 block group relative"
                                  onClick={() => setZoomImage({ url: q.student_image_url, alt: 'Anexo do aluno' })}
                                  data-testid={`essay-student-image-${idx}`}
                                >
                                  <img
                                    src={q.student_image_url}
                                    alt="Anexo da resposta"
                                    className="h-24 rounded-lg border-2 border-white shadow-md object-cover"
                                  />
                                  <span className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ZoomIn className="w-3 h-3" />
                                  </span>
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Expected concepts ("O que o Mestre esperava") */}
                          {q.key_concepts && q.key_concepts.length > 0 && (
                            <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200 p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="w-4 h-4 text-indigo-600" />
                                <span className="text-xs font-bold uppercase tracking-wider text-indigo-700">
                                  O que o Mestre esperava
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {q.key_concepts.map((c) => {
                                  const found = (q.concepts_found || []).some(
                                    (f) => f.toLowerCase() === c.toLowerCase()
                                  );
                                  return (
                                    <span
                                      key={`kc-${idx}-${c}`}
                                      data-testid={`essay-concept-${idx}-${found ? 'found' : 'missing'}`}
                                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${
                                        found
                                          ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                                          : 'bg-white text-slate-500 border-slate-300 line-through decoration-slate-400/60'
                                      }`}
                                    >
                                      {found ? (
                                        <CheckCircle2 className="w-3 h-3" />
                                      ) : (
                                        <X className="w-3 h-3" />
                                      )}
                                      {c}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Motivational narrative tip */}
                          <div
                            data-testid={`essay-narrative-${idx}`}
                            className={`rounded-xl border-l-4 ${meta.border} ${meta.soft} p-3 flex items-start gap-3`}
                          >
                            <div
                              className={`flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br ${meta.gradient} flex items-center justify-center text-white`}
                            >
                              <meta.Icon className="w-4 h-4" />
                            </div>
                            <div>
                              <div className={`text-xs font-bold uppercase tracking-wider ${meta.text}`}>
                                {meta.label}
                              </div>
                              <p className="text-sm text-slate-700 font-medium italic leading-snug">
                                &ldquo;{meta.quote}&rdquo;
                              </p>
                            </div>
                          </div>

                          {/* Teacher/AI feedback */}
                          {(q.teacher_feedback || q.ai_feedback) && (
                            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                                {q.teacher_feedback ? 'Feedback do Mestre' : 'Pré-análise da IA'}
                              </div>
                              <p className="text-sm text-slate-700 leading-relaxed">
                                {q.teacher_feedback || q.ai_feedback}
                              </p>
                              {q.teacher_image_url && (
                                <button
                                  className="mt-2 block group relative"
                                  onClick={() => setZoomImage({ url: q.teacher_image_url, alt: 'Gabarito do professor' })}
                                  data-testid={`essay-teacher-image-${idx}`}
                                >
                                  <img
                                    src={q.teacher_image_url}
                                    alt="Gabarito do professor"
                                    className="h-24 rounded-lg border-2 border-white shadow-md object-cover"
                                  />
                                </button>
                              )}
                            </div>
                          )}

                          {/* Score */}
                          {(graded || typeof q.ai_score === 'number') && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-500">
                                {graded ? 'Nota do Mestre' : 'Nota estimada pela IA'}
                              </span>
                              <span className={`font-bold text-base ${meta.text}`}>
                                {graded ? q.teacher_score : q.ai_score}/100
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // MULTIPLE CHOICE (existing)
                  return (
                  <div 
                    key={`review-q-${q.question_index ?? idx}`} 
                    className={`rounded-xl p-4 border-2 ${
                      q.is_correct 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-red-300 bg-red-50'
                    }`}
                  >
                    <div className="flex items-start gap-2 mb-3">
                      {q.is_correct ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="font-semibold text-slate-800">
                        {q.question_number}. {q.question_text}
                      </div>
                    </div>
                    
                    <div className="grid gap-2 ml-7">
                      {['A', 'B', 'C', 'D'].map(opt => {
                        const optValue = q[`option_${opt.toLowerCase()}`];
                        if (!optValue) return null;
                        
                        const isCorrect = q.correct_answer?.toUpperCase() === opt;
                        const isStudentAnswer = q.student_answer?.toUpperCase() === opt;
                        
                        let bgClass = 'bg-white border border-slate-200';
                        if (isCorrect) {
                          bgClass = 'bg-green-100 border-2 border-green-400';
                        } else if (isStudentAnswer && !isCorrect) {
                          bgClass = 'bg-red-100 border-2 border-red-400';
                        }
                        
                        return (
                          <div key={opt} className={`p-2 rounded-lg text-sm ${bgClass}`}>
                            <span className="font-bold">{opt})</span> {optValue}
                            {isCorrect && <span className="ml-2 text-green-600 font-semibold">✓ Correta</span>}
                            {isStudentAnswer && !isCorrect && <span className="ml-2 text-red-600 font-semibold">✗ Sua resposta</span>}
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="mt-2 ml-7 text-xs text-slate-500">
                      +{q.xp_value} AURA {q.is_correct ? '✓' : '(não ganha)'}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setZoomImage(null)}
          data-testid="image-zoom-modal"
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors"
            onClick={() => setZoomImage(null)}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={zoomImage.url}
            alt={zoomImage.alt}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          {zoomImage.alt && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/50 px-4 py-1 rounded-full">
              {zoomImage.alt}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentMissions;
